import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _ts_generator from "@swc/helpers/src/_ts_generator.mjs";
import { create } from "zustand";
import axios from "axios";
import axiosRetry from "axios-retry";
var createAxiosInstance = function(token) {
    return axios.create({
        headers: {
            Authorization: "Bearer ".concat(token)
        }
    });
};
/**
 * @description
 * Used by **both** `front-ends` and `cinch-nx` to manage reservations.
 * Since axios instances are handled slightly differently in each monorepo, we pass in the instance as a parameter.
 * This instance needs to be set up with the correct headers using `interceptors.request.use`
 * (https://axios-http.com/docs/interceptors).
 *
 * @example
 * const { expiresAt, getReservation, extendReservation } = useReservationStore()
 *
 * const expiresAt = await getReservation(orderId, axiosInstance)
 */ var initialState = {
    reservationRequest: {
        status: "pending"
    },
    expiresAt: undefined
};
export var useReservationStore = create(function(set, get) {
    return _object_spread_props(_object_spread({}, initialState), {
        getReservation: function() {
            var _ref = _async_to_generator(function(orderId, reservationServiceUrl, token) {
                var axiosInstance, ref, expiresAt;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            axiosInstance = createAxiosInstance(token);
                            axiosRetry(axiosInstance, {
                                retries: 6,
                                retryDelay: function(retryCount) {
                                    return retryCount * 1000;
                                },
                                retryCondition: function(error) {
                                    var ref;
                                    return (error === null || error === void 0 ? void 0 : (ref = error.response) === null || ref === void 0 ? void 0 : ref.status) === 404;
                                }
                            });
                            return [
                                4,
                                axiosInstance.get("".concat(reservationServiceUrl, "/").concat(orderId))
                            ];
                        case 1:
                            ref = _state.sent(), expiresAt = ref.data.expiresAt;
                            set({
                                reservationRequest: {
                                    status: "success"
                                },
                                expiresAt: expiresAt
                            });
                            return [
                                2,
                                expiresAt
                            ];
                    }
                });
            });
            return function(orderId, reservationServiceUrl, token) {
                return _ref.apply(this, arguments);
            };
        }(),
        extendReservation: function() {
            var _ref = _async_to_generator(function(orderId, reservationServiceUrl, token) {
                var axiosInstance, response, expiresAt;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            axiosInstance = createAxiosInstance(token);
                            return [
                                4,
                                axiosInstance.post("".concat(reservationServiceUrl, "/").concat(orderId, "/extend"))
                            ];
                        case 1:
                            response = _state.sent();
                            expiresAt = ((response === null || response === void 0 ? void 0 : response.data) || {}).expiresAt;
                            set({
                                reservationRequest: {
                                    status: expiresAt ? "success" : "error"
                                },
                                expiresAt: expiresAt
                            });
                            return [
                                2,
                                response === null || response === void 0 ? void 0 : response.data
                            ];
                    }
                });
            });
            return function(orderId, reservationServiceUrl, token) {
                return _ref.apply(this, arguments);
            };
        }(),
        /**
   * @deprecated Use `extendReservation` instead
   */ legacyExtendReservation: function() {
            var _ref = _async_to_generator(function(orderId, orderServiceUrl, token) {
                var axiosInstance, response;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            axiosInstance = createAxiosInstance(token);
                            return [
                                4,
                                axiosInstance.post("".concat(orderServiceUrl, "/order/extend-expiry"), {
                                    orderId: orderId
                                })
                            ];
                        case 1:
                            response = _state.sent();
                            return [
                                2,
                                response === null || response === void 0 ? void 0 : response.data
                            ];
                    }
                });
            });
            return function(orderId, orderServiceUrl, token) {
                return _ref.apply(this, arguments);
            };
        }(),
        reset: function() {
            return set(initialState);
        }
    });
});
